import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    type SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService, Actives, Filter, FilterOp, PageData, SortMode, SortOrder } from '@homeleite/arch';
import { AnimalSummary, Producer } from '@homeleite/domain';
import { AnimalsApiRepository } from '../../../../../../../../libs/domain/src/lib/animals/animals-api.repository';
import { Observable, Subject, Subscription, forkJoin, lastValueFrom, of, timer } from 'rxjs';
import { catchError, concatMap, debounceTime, distinctUntilChanged, finalize, first, switchMap, take, takeUntil, timeout } from 'rxjs/operators';
import { ProducerDetailsAnimalEventsDialogComponent } from './producer-details-animal-events-dialog/producer-details-animal-events-dialog.component';

const PAGE_SIZE = 48;
const SCROLL_LOAD_PX = 480;
const RESEND_CHUNK = 10;
const RESEND_TIMEOUT_MS = 120000;
const REGISTER_POLL_MS = 3000;
const REGISTER_POLL_TRIES = 6;
const REGISTER_FETCH_TIMEOUT_MS = 10000;

export type AnimalStatusFilter = 'alive' | 'baixa' | 'all' | 'noRegister';

interface RetryResult {
    animalId: string;
    ok: boolean;
    error: string | null;
    info?: string | null;
    register?: string | null;
}

/** Animal ainda sem registro genealógico (não enviado / não retornado pelo SRG-SCL). */
function withoutRegister(animal: AnimalSummary | null | undefined): boolean {
    return !(animal?.register ?? '').trim();
}

/** Nascimento ausente (API grava 01/01/1900): o SRG-SCL não aceita o envio até completar o cadastro. */
function missingBirthdate(animal: AnimalSummary | null | undefined): boolean {
    const time = animal?.birthdate ? new Date(animal.birthdate).getTime() : NaN;
    return Number.isNaN(time) || new Date(time).getUTCFullYear() <= 1900;
}

@Component({
    selector: 'homeleite-producer-details-animals',
    templateUrl: './producer-details-animals.component.html',
    styleUrls: ['./producer-details-animals.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProducerDetailsAnimalsComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input() producer?: Producer;

    public animalsToShow: AnimalSummary[] = [];
    public hasError = false;
    public isLoading = false;
    public isLoadingMore = false;
    public searchTerm = '';
    public statusFilter: AnimalStatusFilter = 'alive';
    public updatingStatus = new Set<string>();
    public aliveCount = 0;
    public baixaCount = 0;
    /** Vivos sem registro (pendentes de SRG-SCL). */
    public noRegisterCount = 0;
    public totalCount = 0;
    /** Animais com reenvio individual em andamento. */
    public resending = new Set<string>();
    /** Reenvio em lote: progresso (null = ocioso). */
    public bulkProgress: { done: number; total: number } | null = null;
    public bulkScanning = false;
    /** Animal com o campo de nascimento aberto no card. */
    public editingBirth: string | null = null;
    public savingBirth = false;
    public readonly today = new Date().toISOString().slice(0, 10);

    private pageFirst = 0;
    private hasMore = true;
    private destroy$ = new Subject<void>();
    private search$ = new Subject<string>();
    private pageSub?: Subscription;
    private scrollRoot: HTMLElement | null = null;
    private readonly onScroll = () => this.checkScrollNearBottom();
    private seenIds = new Set<string>();

    constructor(
        private host: ElementRef<HTMLElement>,
        private cdr: ChangeDetectorRef,
        private animalsApiRepository: AnimalsApiRepository,
        private dialog: MatDialog,
        private snackBar: MatSnackBar,
        private api: ApiService
    ) {
        this.search$
            .pipe(debounceTime(350), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe((term) => {
                this.searchTerm = term;
                this.reload();
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['producer'] && this.producer?.id) {
            this.statusFilter = 'alive';
            this.searchTerm = '';
            this.reload(true);
        }
    }

    ngAfterViewInit(): void {
        this.bindScrollRoot();
        this.checkScrollNearBottom();
    }

    ngOnDestroy(): void {
        this.unbindScrollRoot();
        this.pageSub?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private bindScrollRoot(): void {
        this.unbindScrollRoot();
        const hostEl = this.host.nativeElement;
        const root =
            (hostEl.closest('.mat-mdc-tab-body-content') as HTMLElement | null) ||
            (hostEl.closest('.mat-tab-body-content') as HTMLElement | null) ||
            this.findOverflowParent(hostEl);
        this.scrollRoot = root;
        if (this.scrollRoot) {
            this.scrollRoot.addEventListener('scroll', this.onScroll, { passive: true });
        }
    }

    private unbindScrollRoot(): void {
        this.scrollRoot?.removeEventListener('scroll', this.onScroll);
        this.scrollRoot = null;
    }

    private findOverflowParent(el: HTMLElement | null): HTMLElement | null {
        let node = el?.parentElement ?? null;
        while (node) {
            const oy = getComputedStyle(node).overflowY;
            if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') {
                return node;
            }
            node = node.parentElement;
        }
        return null;
    }

    private checkScrollNearBottom(): void {
        const el = this.scrollRoot;
        if (!el || this.isLoading || this.isLoadingMore || !this.hasMore) {
            return;
        }
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (remaining < SCROLL_LOAD_PX) {
            this.loadMore();
        }
    }

    private producerFilter(): Filter[] {
        return [new Filter('producerId', FilterOp.EQ, this.producer!.id)];
    }

    private activesForFilter(): Actives {
        // Sem registro: só vivos (baixa já saiu do fluxo SRG de cadastro ativo).
        if (this.statusFilter === 'alive' || this.statusFilter === 'noRegister') {
            return Actives.TRUE;
        }
        if (this.statusFilter === 'baixa') {
            return Actives.FALSE;
        }
        return Actives.ALL;
    }

    private get isNoRegisterFilter(): boolean {
        return this.statusFilter === 'noRegister';
    }

    private sorting(): SortOrder[] {
        return [new SortOrder('nickname', SortMode.ASC)];
    }

    private reload(refreshCounts = false): void {
        if (!this.producer?.id) {
            return;
        }
        this.pageSub?.unsubscribe();
        this.pageFirst = 0;
        this.hasMore = true;
        this.isLoadingMore = false;
        this.seenIds.clear();
        this.animalsToShow = [];
        this.hasError = false;
        this.loadPage(true);
        if (refreshCounts || this.aliveCount + this.baixaCount === 0) {
            this.loadCounts();
        } else if (!this.searchTerm) {
            this.syncTotalFromFilter();
        } else {
            this.loadFilteredCount();
        }
    }

    private syncTotalFromFilter(): void {
        if (this.statusFilter === 'alive') {
            this.totalCount = this.aliveCount;
        } else if (this.statusFilter === 'baixa') {
            this.totalCount = this.baixaCount;
        } else if (this.statusFilter === 'noRegister') {
            this.totalCount = this.noRegisterCount;
        } else {
            this.totalCount = this.aliveCount + this.baixaCount;
        }
    }

    private loadCounts(): void {
        const filters = this.producerFilter();
        forkJoin({
            alive: this.animalsApiRepository
                .count('', filters, Actives.TRUE)
                .pipe(catchError(() => of(0))),
            baixa: this.animalsApiRepository
                .count('', filters, Actives.FALSE)
                .pipe(catchError(() => of(0)))
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ alive, baixa }) => {
                this.aliveCount = alive || 0;
                this.baixaCount = baixa || 0;
                if (!this.searchTerm) {
                    this.syncTotalFromFilter();
                }
                this.cdr.markForCheck();
                this.loadNoRegisterCount();
            });
    }

    /** Conta vivos sem registro varrendo páginas (API não tem filtro nativo de register vazio). */
    private loadNoRegisterCount(): void {
        if (!this.producer?.id) {
            return;
        }
        this.scanNoRegister()
            .pipe(
                catchError(() => of([] as AnimalSummary[])),
                takeUntil(this.destroy$)
            )
            .subscribe((list) => {
                this.noRegisterCount = list.length;
                if (!this.searchTerm && this.isNoRegisterFilter) {
                    this.totalCount = this.noRegisterCount;
                }
                this.cdr.markForCheck();
            });
    }

    /** Varre todas as páginas de vivos do produtor e devolve os que ainda não têm registro. */
    private scanNoRegister(first = 0, acc: AnimalSummary[] = []): Observable<AnimalSummary[]> {
        const pageSize = 200;
        return this.animalsApiRepository
            .page(new PageData(first, pageSize), '', this.producerFilter(), this.sorting(), Actives.TRUE)
            .pipe(
                switchMap((batch) => {
                    const list = batch || [];
                    const found = [...acc, ...list.filter(withoutRegister)];
                    return list.length >= pageSize
                        ? this.scanNoRegister(first + list.length, found)
                        : of(found);
                })
            );
    }

    private loadFilteredCount(): void {
        // Sem registro: contagem vem do scan local (API não filtra register vazio).
        if (this.isNoRegisterFilter) {
            this.totalCount = this.noRegisterCount;
            this.cdr.markForCheck();
            return;
        }
        this.animalsApiRepository
            .count(this.searchTerm, this.producerFilter(), this.activesForFilter())
            .pipe(
                catchError(() => of(0)),
                takeUntil(this.destroy$)
            )
            .subscribe((n) => {
                this.totalCount = n || 0;
                this.cdr.markForCheck();
            });
    }

    private loadPage(reset: boolean): void {
        if (!this.producer?.id) {
            return;
        }
        if (!reset && (this.isLoadingMore || this.isLoading || !this.hasMore)) {
            return;
        }

        this.pageSub?.unsubscribe();

        if (reset) {
            this.isLoading = true;
            this.isLoadingMore = false;
        } else {
            this.isLoadingMore = true;
        }
        this.cdr.markForCheck();

        const page = new PageData(this.pageFirst, PAGE_SIZE);
        this.pageSub = this.animalsApiRepository
            .page(
                page,
                this.searchTerm,
                this.producerFilter(),
                this.sorting(),
                this.activesForFilter()
            )
            .subscribe({
                next: (animals) => {
                    const batch = animals || [];
                    const fresh: AnimalSummary[] = [];
                    for (const a of batch) {
                        const id = a?.id;
                        if (!id || this.seenIds.has(id)) {
                            continue;
                        }
                        if (this.isNoRegisterFilter && !withoutRegister(a)) {
                            continue;
                        }
                        this.seenIds.add(id);
                        fresh.push(a);
                    }

                    this.animalsToShow = reset ? fresh : [...this.animalsToShow, ...fresh];
                    this.pageFirst += batch.length;
                    // Sem página nova, ou API devolveu só duplicatas → para.
                    // Sem registro: pode precisar de mais páginas do servidor para encher a grade.
                    const serverHasMore = batch.length >= PAGE_SIZE;
                    this.hasMore =
                        serverHasMore &&
                        (fresh.length > 0 || this.isNoRegisterFilter);

                    this.isLoading = false;
                    this.isLoadingMore = false;
                    this.cdr.markForCheck();

                    if (!this.scrollRoot) {
                        this.bindScrollRoot();
                    }
                    // Sem registro: se o lote veio quase vazio, busca o próximo na hora.
                    queueMicrotask(() => {
                        if (
                            this.isNoRegisterFilter &&
                            this.hasMore &&
                            this.animalsToShow.length < PAGE_SIZE &&
                            !this.isLoading &&
                            !this.isLoadingMore
                        ) {
                            this.loadMore();
                            return;
                        }
                        this.checkScrollNearBottom();
                    });
                },
                error: (err) => {
                    console.error('Erro ao buscar animais do produtor', err);
                    this.hasError = reset && this.animalsToShow.length === 0;
                    this.isLoading = false;
                    this.isLoadingMore = false;
                    this.cdr.markForCheck();
                }
            });
    }

    public loadMore(): void {
        if (this.isLoading || this.isLoadingMore || !this.hasMore) {
            return;
        }
        this.loadPage(false);
    }

    public search(event: Event): void {
        const term = (event.target as HTMLInputElement).value.toLowerCase().trim();
        this.search$.next(term);
    }

    public setStatusFilter(filter: AnimalStatusFilter): void {
        if (this.statusFilter === filter) {
            return;
        }
        this.statusFilter = filter;
        this.reload(false);
    }

    public animalCode(animal: AnimalSummary): string {
        const nick = (animal.nickname || '').trim();
        const match = nick.match(/^(\d+)\b/);
        return match ? match[1] : nick || '—';
    }

    public animalName(animal: AnimalSummary): string {
        const nick = (animal.nickname || '').trim();
        const name = (animal.name || '').trim();
        if (name && name !== nick) {
            return name;
        }
        const rest = nick.match(/^\d+\s+(.+)$/);
        return rest ? rest[1] : '';
    }

    public get bulkBusy(): boolean {
        return this.bulkScanning || this.bulkProgress !== null;
    }

    public needsResend(animal: AnimalSummary): boolean {
        return animal.isActive && withoutRegister(animal) && !missingBirthdate(animal);
    }

    public isIncomplete(animal: AnimalSummary): boolean {
        return animal.isActive && withoutRegister(animal) && missingBirthdate(animal);
    }

    public resendOne(animal: AnimalSummary): void {
        if (this.resending.has(animal.id) || this.bulkBusy) {
            return;
        }
        this.resending.add(animal.id);
        this.cdr.markForCheck();
        this.postRetry([animal.id])
            .pipe(
                finalize(() => {
                    this.resending.delete(animal.id);
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (results) => {
                    const result = results[0];
                    if (result?.ok) {
                        this.snackBar.open(result.info ?? 'Animal enviado ao SRG-SCL.', 'OK', { duration: 5000 });
                    } else {
                        this.snackBar.open(`Não foi possível enviar: ${result?.error ?? 'sem resposta do servidor'}`, 'Fechar', { duration: 8000 });
                    }
                    this.refreshAnimal(animal);
                },
                error: () => {
                    this.snackBar.open('Falha ao enviar ao SRG-SCL (API indisponível ou tempo esgotado).', 'Fechar', { duration: 6000 });
                    this.refreshAnimal(animal);
                }
            });
    }

    /** O registro chega pelo webhook segundos após o envio automático: aguarda antes de pedir reenvio. */
    private awaitRegister(animal: AnimalSummary): void {
        this.resending.add(animal.id);
        this.cdr.markForCheck();
        timer(REGISTER_POLL_MS, REGISTER_POLL_MS)
            .pipe(
                take(REGISTER_POLL_TRIES),
                concatMap(() => this.fetchSummary(animal.id)),
                first((fresh) => !!fresh && !withoutRegister(fresh), null),
                takeUntil(this.destroy$),
                finalize(() => {
                    this.resending.delete(animal.id);
                    this.cdr.markForCheck();
                })
            )
            .subscribe((fresh) => {
                this.resending.delete(animal.id);
                if (fresh) {
                    this.applyFresh(animal, fresh);
                    this.snackBar.open(`Nascimento salvo · registro ${fresh.register} recebido do SRG-SCL.`, 'OK', { duration: 6000 });
                } else {
                    this.resendOne(animal);
                }
            });
    }

    private fetchSummary(id: string): Observable<AnimalSummary | null> {
        return this.animalsApiRepository.findSummary(id).pipe(
            timeout(REGISTER_FETCH_TIMEOUT_MS),
            catchError(() => of(null))
        );
    }

    /** Recarrega só o card: nunca deixa a tela com o animal desatualizado (ex.: registro já recebido). */
    private refreshAnimal(animal: AnimalSummary): void {
        this.fetchSummary(animal.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe((fresh) => fresh && this.applyFresh(animal, fresh));
    }

    private applyFresh(animal: AnimalSummary, fresh: AnimalSummary): void {
        const gotRegister = withoutRegister(animal) && !withoutRegister(fresh);
        if (gotRegister && animal.isActive) {
            this.noRegisterCount = Math.max(0, this.noRegisterCount - 1);
        }
        if (gotRegister && this.isNoRegisterFilter) {
            this.animalsToShow = this.animalsToShow.filter((a) => a.id !== animal.id);
            this.seenIds.delete(animal.id);
            this.totalCount = Math.max(0, this.totalCount - 1);
        } else {
            this.animalsToShow = this.animalsToShow.map((a) => (a.id === animal.id ? fresh : a));
        }
        this.cdr.markForCheck();
    }

    public saveBirthdate(animal: AnimalSummary, value: string): void {
        if (this.savingBirth) {
            return;
        }
        if (!value || value < '1901-01-01' || value > this.today) {
            this.snackBar.open('Informe uma data de nascimento válida (não pode ser no futuro).', 'Fechar', { duration: 6000 });
            return;
        }
        this.savingBirth = true;
        this.cdr.markForCheck();
        this.api.root
            .path('milk-adjustments', 'animal-birthdate')
            .postJson({ animalId: animal.id, birthdate: value })
            .pipe(
                timeout(RESEND_TIMEOUT_MS),
                finalize(() => {
                    this.savingBirth = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    animal.birthdate = new Date(`${value}T12:00:00Z`);
                    this.editingBirth = null;
                    this.awaitRegister(animal);
                },
                error: (err) => {
                    const msg = typeof err?.error === 'string' ? err.error : err?.error?.message;
                    this.snackBar.open(`Não foi possível salvar o nascimento${msg ? `: ${msg}` : '.'}`, 'Fechar', { duration: 8000 });
                }
            });
    }

    public async resendAll(): Promise<void> {
        if (this.bulkBusy) {
            return;
        }
        this.bulkScanning = true;
        this.cdr.markForCheck();
        let ids: string[];
        let incomplete = 0;
        try {
            const found = await lastValueFrom(this.scanNoRegister());
            ids = found.filter((a) => !missingBirthdate(a)).map((a) => a.id);
            incomplete = found.length - ids.length;
        } catch {
            this.snackBar.open('Não foi possível listar os animais sem registro.', 'Fechar', { duration: 6000 });
            return;
        } finally {
            this.bulkScanning = false;
            this.cdr.markForCheck();
        }
        const incompleteMsg = incomplete > 0 ? `${incomplete} sem data de nascimento (informe pelo botão "Sem nasc.")` : '';
        if (ids.length === 0) {
            this.snackBar.open(incompleteMsg ? `Nada a enviar: ${incompleteMsg}.` : 'Nenhum animal vivo sem registro.', 'OK', { duration: 8000 });
            return;
        }
        if (!confirm(`Reenviar ${ids.length} animal(is) sem registro ao SRG-SCL?${incompleteMsg ? `
${incompleteMsg} ficarão de fora.` : ''}`)) {
            return;
        }

        this.bulkProgress = { done: 0, total: ids.length };
        this.cdr.markForCheck();
        const results: RetryResult[] = [];
        let interrupted = false;
        try {
            for (let i = 0; i < ids.length; i += RESEND_CHUNK) {
                const batch = await lastValueFrom(this.postRetry(ids.slice(i, i + RESEND_CHUNK)));
                results.push(...batch);
                this.bulkProgress = { done: results.length, total: ids.length };
                this.cdr.markForCheck();
            }
        } catch {
            interrupted = true;
        } finally {
            this.bulkProgress = null;
            this.cdr.markForCheck();
        }
        this.reportBulk(results, ids.length, interrupted, incomplete);
        this.reload(true);
    }

    private postRetry(animalIds: string[]): Observable<RetryResult[]> {
        return this.api.root
            .path('integration-sync', 'retry')
            .postJson<RetryResult[]>({ animalIds })
            .pipe(timeout(RESEND_TIMEOUT_MS));
    }

    private reportBulk(results: RetryResult[], total: number, interrupted: boolean, incomplete = 0): void {
        const ok = results.filter((r) => r.ok).length;
        const failed = results.length - ok;
        const firstError = results.find((r) => !r.ok)?.error;
        const parts = [`${ok} de ${total} enviados ao SRG-SCL`];
        if (failed > 0) {
            parts.push(`${failed} com erro${firstError ? `: ${firstError}` : ''}`);
        }
        if (interrupted) {
            parts.push(`${total - results.length} não enviados (sem resposta do servidor)`);
        }
        if (incomplete > 0) {
            parts.push(`${incomplete} sem data de nascimento (informe pelo botão "Sem nasc.")`);
        }
        this.snackBar.open(parts.join(' · '), 'Fechar', { duration: 10000 });
    }

    public onViewEvents(animal: AnimalSummary): void {
        const code = this.animalCode(animal);
        const name = this.animalName(animal);
        const title = name && name !== code ? `${code} · ${name}` : code || name || 'Animal';
        this.dialog.open(ProducerDetailsAnimalEventsDialogComponent, {
            width: '920px',
            maxWidth: '96vw',
            height: '88vh',
            maxHeight: '88vh',
            autoFocus: false,
            data: {
                animalId: animal.id,
                animalName: title,
                animalRegister: animal.register || '',
                producerId: this.producer?.id || ''
            }
        });
    }

    public onToggleActiveState(animal: AnimalSummary): void {
        const originalState = animal.isActive;
        const animalId = animal.id;

        this.updatingStatus.add(animalId);
        animal.isActive = !animal.isActive;

        this.animalsApiRepository.updateActiveState(animalId, animal.isActive).subscribe({
            next: (updatedAnimal) => {
                animal.isActive = updatedAnimal.isActive;
                if (originalState && !updatedAnimal.isActive) {
                    this.aliveCount = Math.max(0, this.aliveCount - 1);
                    this.baixaCount += 1;
                } else if (!originalState && updatedAnimal.isActive) {
                    this.baixaCount = Math.max(0, this.baixaCount - 1);
                    this.aliveCount += 1;
                }
                if (withoutRegister(animal)) {
                    if (originalState && !updatedAnimal.isActive) {
                        this.noRegisterCount = Math.max(0, this.noRegisterCount - 1);
                    } else if (!originalState && updatedAnimal.isActive) {
                        this.noRegisterCount += 1;
                    }
                }
                if (
                    (this.statusFilter === 'alive' && !updatedAnimal.isActive) ||
                    (this.statusFilter === 'baixa' && updatedAnimal.isActive) ||
                    (this.statusFilter === 'noRegister' && !updatedAnimal.isActive)
                ) {
                    this.animalsToShow = this.animalsToShow.filter((a) => a.id !== animalId);
                    this.seenIds.delete(animalId);
                    this.totalCount = Math.max(0, this.totalCount - 1);
                } else if (!this.searchTerm) {
                    this.syncTotalFromFilter();
                }
                this.cdr.markForCheck();
            },
            error: (err) => {
                animal.isActive = originalState;
                console.error('Falha ao atualizar status do animal:', err);
                const action = originalState ? 'desativar' : 'ativar';
                this.snackBar.open(`Erro ao ${action} o animal. Tente novamente.`, 'Fechar', {
                    duration: 5000
                });
                this.cdr.markForCheck();
            },
            complete: () => {
                this.updatingStatus.delete(animalId);
                this.cdr.markForCheck();
            }
        });
    }

    get canLoadMore(): boolean {
        return this.hasMore;
    }

    get emptyMessage(): string {
        if (this.searchTerm) {
            return 'Nenhum resultado para a busca.';
        }
        if (this.statusFilter === 'alive') {
            return 'Nenhum animal vivo neste produtor.';
        }
        if (this.statusFilter === 'baixa') {
            return 'Nenhum animal com baixa.';
        }
        if (this.statusFilter === 'noRegister') {
            return 'Nenhum animal vivo sem registro (todos já têm registro SRG-SCL).';
        }
        return 'Nenhum animal cadastrado.';
    }
}
